/**
 * The minimum a member needs for the last-Owner rule. Both `getOrganizationUsers`
 * and `getProjectUsers` return objects that satisfy this shape.
 */
export type MemberForOwnerCheck = {
  is_verified?: boolean | null;
  role_id?: number | null;
};

/**
 * Decides whether removing a role would leave an organization or project with no
 * active Owner.
 *
 * Only an Owner can be the last Owner, so the member list is fetched only when the
 * role being removed is Owner. Since AKL-843 the member-list endpoints require the
 * user-details permission that only Owners and Editors hold, so a Contributor, Tagger
 * or Viewer removing their own role must not trigger the fetch at all: it would 403
 * and the removal would never happen.
 *
 * @param roleBeingRemoved the role id about to be removed
 * @param ownerRole the Owner role id for this scope
 * @param listMembers fetches the members of the organization or project being left
 * @returns true when the role is Owner and exactly one verified Owner exists
 * @throws whatever `listMembers` throws, so the caller can report the failure
 */
export async function isRemovingLastOwner(
  roleBeingRemoved: number,
  ownerRole: number,
  listMembers: () => Promise<{ users: MemberForOwnerCheck[] }>,
): Promise<boolean> {
  if (roleBeingRemoved !== ownerRole) {
    return false;
  }
  const { users } = await listMembers();
  const verifiedOwners = users.filter(
    (member) => member.is_verified && member.role_id === ownerRole,
  );
  return verifiedOwners.length === 1;
}
